import { useCallback, useMemo, useState } from 'react'
import { Alert, Button, Descriptions, Drawer, Modal, Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { PageHeader } from '@/shared/ui/PageHeader'
import { NasDeleteModal } from '@/features/nas/NasDeleteModal'
import { NasFormModal } from '@/features/nas/NasFormModal'
import { NasHealthDetails } from '@/features/nas/NasHealthDetails'
import { NasHealthTag } from '@/features/nas/NasHealthTag'
import { NasSecretModal } from '@/features/nas/NasSecretModal'
import { NasZoneLinks } from '@/features/nas/NasZoneLinks'
import { useNasHealth } from '@/features/nas/useNasHealth'
import { probeNas, type ProbeResult, fetchNasPage, looksLikeIp, type NasEntry } from '@/features/nas/nasApi'
import { RefreshIndicator } from '@/shared/ui/RefreshIndicator'
import { SiteRef } from '@/features/sites/SiteRef'

/**
 * NAS — RADIUS klientlari (MikroTik routerlar, hotspot nuqtalari).
 *
 * <h2>⚠️ BU SAHIFA INFRATUZILMA DARAJASIDA XAVFLI</h2>
 * FreeRADIUS faqat `nas` jadvalida RO'YXATDAN O'TGAN qurilmadan kelgan
 * so'rovni ko'radi. Bu yerdagi bitta qator — o'sha router ortidagi hamma
 * abonentning umuman ulana olishi. Shuning uchun:
 *
 *   · o'chirish alohida modal, oqibati matn bilan aytilgan va tasdiqlash
 *     uchun qisqa nom qo'lda yoziladi (`NasDeleteModal`)
 *   · sir hech qayerda KO'RSATILMAYDI — faqat almashtiriladi
 *     (`NasSecretModal`, sabab `nasApi.ts` sarlavhasida)
 *   · har amal o'z huquqiga bog'langan (`nas.create/.update/.delete`)
 *
 * <h2>Nega `DataTable`, oddiy `Table` emas</h2>
 * Ro'yxat SERVER TOMONDA sahifalanadi (`GET /api/nas?q&page&size`) va
 * `NasPage` haqiqiy `total` beradi — ya'ni jadval sahifalar sonini
 * taxmin qilmaydi (`showTotal` yoqilgan holicha qoladi, `AuditPage` dan
 * farqli). Qidiruv ham SERVERDA — shuning uchun `searchable` yoqilgan
 * (sabab `nasApi.fetchNasPage` javadoc'ida).
 *
 * <h2>⚠️ Ro'yxat FAQAT o'z tashkilotining qurilmalarini ko'rsatadi</h2>
 * Ilgari `nas.read` huquqi bor har qanday operator BARCHA tashkilotlarning
 * routerlarini ko'rar, tahrirlar va o'chirardi — `nas` FreeRADIUS jadvali
 * va unda `tenant_id` ustuni yo'q. Doira endi backendda
 * `platform.nas_tenant` (RLS) orqali qo'yilgan; panelda buni ko'rsatuvchi
 * qo'shimcha filtr YO'Q va bo'lmasligi ham kerak — "tashkilot" tanlovini
 * chizish uni tanlash MUMKINDEK ko'rsatardi.
 *
 * <h2>⚠️ HOLAT USTUNI — nega u qo'shildi</h2>
 * Bu sahifa uzoq vaqt FAQAT statik sozlamani ko'rsatdi. Oqibati amalda
 * ko'rildi: jonli MikroTik 12:54 da qayta yuklandi va buni hech kim
 * bilmadi — hodisa faqat `radacct` ni QO'LDA o'qib topildi. Endi tiriklik
 * belgisi ustunda va kartochkada (`useNasHealth`).
 *
 * Belgi QURILMAGA SO'ROV YUBORMAYDI — u FreeRADIUS accounting
 * yozuvlaridan olinadi (`GET /api/nas/health`).
 *
 * <h2>⚠️ BO'SH RO'YXAT — halokat, oddiy "topilmadi" emas</h2>
 * `nas` jadvali bo'sh bo'lsa FreeRADIUS hech bir klientni tanimaydi va
 * HAR paketni jimgina tashlab yuboradi: log'da ham, panelda ham hech
 * qanday xato ko'rinmaydi, abonentlar esa umuman ulana olmaydi. Bu amalda
 * sodir bo'lgan va tasodifan topilgan. Shuning uchun bo'sh reyestr bo'sh
 * jadval sifatida emas, OCHIQ xato sifatida ko'rsatiladi.
 */
export function NasPage() {
  const { t } = useTranslation()
  const health = useNasHealth()

  const [selected, setSelected] = useState<NasEntry | null>(null)

  /*
    ⚠️ Natija MODALDA ko'rsatiladi, jadval katagida emas.

    Javob bir jumlali "tirik/o'lik" emas: javob bo'lmasa sabab IKKITA
    bo'lishi mumkin (qurilma o'chiq YOKI sir noto'g'ri) va operator
    aynan shu farqni bilishi kerak. Tor katakda bu matn sig'masdi va
    qisqartirilgan "javob yo'q" degan yozuv uni "qurilma o'chiq" deb
    o'ylashga majbur qilardi — bu esa bir necha soat noto'g'ri
    yo'nalishda izlanish demak.
  */
  const [probing, setProbing] = useState<string | null>(null)
  const [probeResult, setProbeResult] = useState<{ nasIp: string; result: ProbeResult } | null>(null)

  // ⚠️ `useCallback` — ustunlar `useMemo` ichida va u eskirgan
  // funksiyani ushlab qolmasligi kerak (eslint buni ogohlantirgan).
  const runProbe = useCallback((nasIp: string) => {
    setProbing(nasIp)
    probeNas(nasIp)
      .then((status) =>
        setProbeResult({ nasIp, result: { status, detail: t(`nas.probeDetail.${status}`) } }),
      )
      .catch((err: unknown) =>
        setProbeResult({
          nasIp,
          // Relay javob bermasa ham natija KO'RSATILADI: jimgina
          // hech narsa bo'lmasligi "tugma ishlamadi" bo'lib o'qilardi.
          result: { status: 'RELAY_DOWN', detail: translateError(err, t) },
        }),
      )
      .finally(() => setProbing(null))
  }, [t])

  const [editing, setEditing] = useState<NasEntry | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [secretFor, setSecretFor] = useState<NasEntry | null>(null)
  const [deleting, setDeleting] = useState<NasEntry | null>(null)

  const columns = useMemo<ColumnsType<NasEntry>>(
    () => [
      {
        key: 'shortname',
        title: t('nas.shortname'),
        width: 220,
        render: (_, row) => <Typography.Text strong>{row.shortname}</Typography.Text>,
      },
      {
        // ⚠️ Holat ustuni ATAYLAB IKKINCHI — nomdan keyin, hamma
        // sozlamadan oldin. Operator sahifani ochganda birinchi qidiradigan
        // narsa "nima ishlamayapti", `ports`/`community` emas. Oxiriga
        // qo'yilsa u gorizontal scroll ortida qolardi.
        key: 'health',
        title: t('nas.health.column'),
        width: 170,
        // Saralash QO'YILMAGAN: backend har doim `shortname` bo'yicha
        // qaytaradi va mijoz tomonidagi saralash faqat JORIY sahifani
        // tartiblab, "eng yomon holat yuqorida" degan yolg'on va'da
        // berardi (`nasApi.fetchNasPage` dagi bilan bir xil sabab).
        render: (_, row) => (
          <NasHealthTag
            health={health.byId.get(row.id)}
            windowSeconds={health.windowSeconds}
            loading={health.query.isPending}
          />
        ),
      },
      {
        key: 'nasname',
        title: t('nas.nasname'),
        width: 200,
        render: (_, row) => (
          <Space size={6}>
            <Typography.Text code copyable={{ text: row.nasname }}>
              {row.nasname}
            </Typography.Text>
            {/* Host nomi ham, IP ham ruxsat etilgan — qaysi ekani ko'rinib
                tursin, chunki DNS orqali yozilgan NAS nomi o'zgarganda
                autentifikatsiya jimgina to'xtaydi */}
            {looksLikeIp(row.nasname) ? null : <Tag>{t('nas.hostname')}</Tag>}
          </Space>
        ),
      },
      {
        key: 'type',
        title: t('nas.type'),
        width: 120,
        render: (_, row) => row.type ?? t('common.dash'),
      },
      {
        key: 'ports',
        title: t('nas.ports'),
        width: 100,
        align: 'right',
        render: (_, row) => row.ports ?? t('common.dash'),
      },
      {
        // ⚠️ Joylashuv ustuni HECH QACHON bo'sh qolmaydi: `SiteRef`
        // "ko'rsatilmagan" va "noma'lum" holatlarini ALOHIDA chizadi. Bo'sh
        // katak ikkalasini ham yashirardi — aynan shu narsa uzilish paytida
        // "bu router qayerda?" degan savolni javobsiz qoldirardi.
        key: 'site',
        title: t('nas.site'),
        width: 230,
        render: (_, row) => <SiteRef siteId={row.siteId} />,
      },
      {
        key: 'description',
        title: t('nas.description'),
        render: (_, row) => row.description ?? t('common.dash'),
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          /*
            ⚠️ "Tekshirish" — YUQORIDAGI holat ustunidan BOSHQA savol.
            Ustun accounting bo'yicha ("qurilma bizga yozyaptimi"), bu
            amal esa boshqaruv kanalini uradi ("biz unga yeta olamizmi
            va sir to'g'rimi"). Ikkalasi bir vaqtda turlicha bo'lishi
            mumkin va aynan shu holat jonli o'rnatmada bir necha soat
            izlanishga sabab bo'lgan.

            ⚠️ Ikkala amal ham HUQUQSIZ va shunday qoladi: ro'yxatni
            ko'ra olgan odam qurilmani tekshira ham, kartochkasini ocha
            ham oladi. Tahrirlash/sir/o'chirish — kartochka ichida, o'z
            huquqlari bilan.
          */
          <RowActions
            items={[
              {
                key: 'probe',
                label: t('nas.probe'),
                onClick: () => runProbe(row.nasname),
                loading: probing === row.nasname,
              },
              {
                key: 'open',
                label: t('nas.open'),
                onClick: () => setSelected(row),
              },
            ]}
          />
        ),
      },
    ],
    [t, health.byId, health.windowSeconds, health.query.isPending, probing, runProbe],
  )

  return (
    <>
      {/*
        ⚠️ Natija turi bo'yicha RANG: tirik — yashil, javob yo'q —
        sariq (xato emas, aniqlanmagan holat), relay o'lgan — qizil,
        chunki oxirgisi bizning tomondagi nosozlik.
      */}
      <Modal
        open={probeResult !== null}
        title={t('nas.probeTitle', { nasIp: probeResult?.nasIp ?? '' })}
        onCancel={() => setProbeResult(null)}
        onOk={() => setProbeResult(null)}
        okText={t('common.close')}
        cancelButtonProps={{ style: { display: 'none' } }}
        destroyOnHidden
      >
        {probeResult ? (
          <Alert
            type={
              probeResult.result.status === 'ALIVE'
                ? 'success'
                : probeResult.result.status === 'UNREACHABLE'
                  ? 'warning'
                  : 'error'
            }
            showIcon
            message={t(`nas.probeStatus.${probeResult.result.status}`)}
            description={probeResult.result.detail}
          />
        ) : null}
      </Modal>

      <PageHeader
        title={t('nav.nas')}
        description={t('pageIntro.nas')}
        extra={
          <Can permission="nas.create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('nas.createTitle')}
            </Button>
          </Can>
        }
      />

      {/*
        ⚠️ BO'SH REYESTR — ENG YUQORIDA va QIZIL.

        Bu "hozircha qurilma qo'shilmagan" degan ma'lumot emas: bunday
        holatda FreeRADIUS hech bir klientni tanimaydi va har paketni
        JIMGINA tashlab yuboradi — abonentlar ulana olmaydi, lekin na
        log'da, na panelda xato ko'rinmaydi. Bugun aynan shu bo'ldi va
        tasodifan topildi.

        ⚠️ Manba `health.registryEmpty`, jadvalning bo'shligi EMAS: jadval
        qidiruv tufayli ham bo'sh bo'lishi mumkin va "topilmadi" ni
        "qurilma yo'q" bilan chalkashtirish yolg'on trevoga bo'lardi.
        Yuklanish va xato paytida (`null`) ham ko'rsatilmaydi.
      */}
      {health.registryEmpty === true ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('nas.health.emptyRegistryTitle')}
          description={t('nas.health.emptyRegistryText')}
        />
      ) : null}

      {/* Sahifaning O'ZI nima uchun xavfli ekanini bir marta aytadi —
          har modal ichida takrorlanishini kutmasdan */}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('nas.pageWarnTitle')}
        description={t('nas.pageWarnText')}
      />

      {/*
        ⚠️ Belgi so'rovi yiqilsa BU AYTILADI. Ustunda "—" turishi
        yetarli emasdi: bo'sh katak "hammasi joyida" bo'lib o'qiladi.
        Xabar ayni paytda nima ISHLAYOTGANINI ham aytadi — ro'yxat va
        boshqaruv bu so'rovga bog'liq emas.
      */}
      {health.query.isError ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('nas.health.unavailable')}
        />
      ) : null}

      <DataTable<NasEntry>
        queryKey={queryKeys.nas.all}
        fetchPage={fetchNasPage}
        columns={columns}
        rowKey={(row) => String(row.id)}
        tableId="nas"
        scrollX={1410}
        stickyFirstColumn
        searchable
        searchPlaceholder={t('nas.searchPlaceholder')}
        emptyTitle={t('nas.empty')}
        emptyDescription={t('nas.emptyHint')}
        onRow={(row) => ({ style: { cursor: 'pointer' }, onClick: () => setSelected(row) })}
        /*
          ⚠️ Ko'rsatkich HOLAT so'rovining vaqtini aytadi, ro'yxatnikini
          emas — ekrandagi "Aloqada" yozuvi qachonlik ekani aynan shu.
          Ro'yxatning o'zi avtomatik yangilanmaydi (`useNasHealth`
          izohidagi sabab), shuning uchun `DataTable.refetchInterval`
          ATAYLAB qo'yilmagan.
        */
        toolbarExtra={
          health.query.dataUpdatedAt ? (
            <RefreshIndicator
              updatedAt={health.query.dataUpdatedAt}
              fetching={health.query.isFetching}
            />
          ) : null
        }
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        width={520}
        title={selected?.shortname ?? ''}
        extra={
          <Space>
            <Can permission="nas.update">
              <Button
                size="small"
                onClick={() => {
                  setEditing(selected)
                  setFormOpen(true)
                }}
              >
                {t('nas.edit')}
              </Button>
            </Can>
            <Can permission="nas.update">
              <Button size="small" danger onClick={() => setSecretFor(selected)}>
                {t('nas.secretChange')}
              </Button>
            </Can>
            <Can permission="nas.delete">
              <Button size="small" danger type="primary" onClick={() => setDeleting(selected)}>
                {t('nas.delete')}
              </Button>
            </Can>
          </Space>
        }
      >
        {selected ? (
          <>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={t('nas.nasname')}>
                <Typography.Text code copyable={{ text: selected.nasname }}>
                  {selected.nasname}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('nas.shortname')}>{selected.shortname}</Descriptions.Item>
              <Descriptions.Item label={t('nas.type')}>
                {selected.type ?? t('common.dash')}
              </Descriptions.Item>
              <Descriptions.Item label={t('nas.ports')}>
                {selected.ports ?? t('common.dash')}
              </Descriptions.Item>
              <Descriptions.Item label={t('nas.server')}>
                {selected.server ?? t('common.dash')}
              </Descriptions.Item>
              <Descriptions.Item label={t('nas.community')}>
                {selected.community ?? t('common.dash')}
              </Descriptions.Item>
              <Descriptions.Item label={t('nas.site')}>
                <SiteRef siteId={selected.siteId} />
              </Descriptions.Item>
              <Descriptions.Item label={t('nas.description')}>
                {selected.description ?? t('common.dash')}
              </Descriptions.Item>
              {/*
                ⚠️ SIR QATORI ATAYLAB SHUNDAY: qiymat emas, uning
                KO'RSATILMASLIGI sababi yoziladi. Bo'sh katak "sir
                qo'yilmagan" degan yolg'on xulosaga olib kelardi — sir
                esa MAJBURIY va doim mavjud (`NasDraft`).
              */}
              <Descriptions.Item label={t('nas.secret')}>
                <Space direction="vertical" size={2}>
                  <Typography.Text type="secondary" italic>
                    {t('nas.secretNeverShown')}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('nas.secretNeverShownHint')}
                  </Typography.Text>
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <div style={{ height: 24 }} />

            {/* ⚠️ Holat SOZLAMADAN KEYIN, lekin zonalardan OLDIN: kartochka
                ochilishining eng ko'p uchraydigan sababi "bu qurilma bilan
                nima bo'lyapti" degan savol */}
            <NasHealthDetails
              health={health.byId.get(selected.id)}
              windowSeconds={health.windowSeconds}
              loading={health.query.isPending}
              failed={health.query.isError}
            />

            <div style={{ height: 24 }} />

            <NasZoneLinks nas={selected} enabled={selected !== null} />
          </>
        ) : null}
      </Drawer>

      <NasFormModal
        open={formOpen}
        nas={editing}
        onClose={() => {
          setFormOpen(false)
          // Kartochka ochiq bo'lsa u eskirgan nusxani ko'rsatib turardi —
          // ro'yxat yangilanadi, kartochka esa yopiladi
          setSelected(null)
        }}
      />

      <NasSecretModal
        open={secretFor !== null}
        nas={secretFor}
        onClose={() => setSecretFor(null)}
      />

      <NasDeleteModal
        open={deleting !== null}
        nas={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null)
          setSelected(null)
        }}
      />
    </>
  )
}
